import { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import PdfViewerContainer from "../../../components/PdfViewerContainer";

const SELECT = "SELECT";
const FORM_NAME = "SBIKYC3";
const PDF_ENDPOINT = "/FillMyForm/SBIKYC3/Print.php";

const applicantFields = [
  { name: "branch", label: "Branch Name", maxLength: 18, required: true },
  { name: "bcode", label: "Branch Code", maxLength: 5, required: true },
  { name: "cifno", label: "CIF Number", maxLength: 11, numeric: true },
  { name: "acno", label: "Account Number", maxLength: 11, numeric: true },
  {
    name: "title",
    label: "Title",
    type: "select",
    required: true,
    options: [
      ["MR", "MR"],
      ["MRS", "MRS"],
      ["MISS", "MISS"],
    ],
  },
  { name: "name", label: "Applicant Name", maxLength: 36, alphaOnly: true, required: true },
  {
    name: "ftitle",
    label: "Father / Mother Title",
    type: "select",
    options: [
      ["MR", "MR"],
      ["MRS", "MRS"],
    ],
  },
  { name: "fname", label: "Father / Mother Name", maxLength: 36, alphaOnly: true },
  { name: "dob", label: "Date of Birth", type: "date", required: true },
  {
    name: "gender",
    label: "Gender",
    type: "select",
    required: true,
    options: [
      ["MALE", "MALE"],
      ["FEMALE", "FEMALE"],
    ],
  },
  {
    name: "mstatus",
    label: "Marital Status",
    type: "select",
    required: true,
    options: [
      ["MARRIED", "MARRIED"],
      ["UNMARRIED", "UNMARRIED"],
    ],
  },
  {
    name: "aadhaar",
    label: "Aadhaar Number",
    maxLength: 12,
    numeric: true,
    required: true,
  },
  { name: "pan", label: "PAN Number", maxLength: 10, uppercase: true },
  { name: "mobile", label: "Mobile", maxLength: 10, numeric: true },
];

const addressFields = [
  { name: "add1", label: "Address Line 1", maxLength: 39 },
  { name: "add2", label: "Address Line 2", maxLength: 39 },
  { name: "city", label: "City", maxLength: 13 },
  { name: "dist", label: "District", maxLength: 21 },
  { name: "state", label: "State", maxLength: 13 },
  { name: "pin", label: "PIN", maxLength: 6, numeric: true },
  {
    name: "qualification",
    label: "Qualification",
    type: "select",
    options: [
      ["BSSC", "BELOW SSC"],
      ["SSC", "SSC"],
      ["HSC", "HSC"],
      ["GRADUATE", "GRADUATE"],
      ["PGRADUATE", "POST GRADUATE"],
      ["PROFESSIONAL", "PROFESSIONAL"],
      ["OTHERS", "OTHERS"],
    ],
  },
  {
    name: "occupation",
    label: "Occupation",
    type: "select",
    options: [
      ["SERVICE", "SERVICE"],
      ["PRIVATE_SECTOR", "PRIVATE SECTOR"],
      ["PUBLIC_SECTOR", "PUBLIC SECTOR"],
      ["GOVERNMENT_SECTOR", "GOVERNMENT SECTOR"],
      ["OTHERS", "OTHERS"],
      ["PROFESSIONAL", "PROFESSIONAL"],
      ["SELF_EMPLOYED", "SELF EMPLOYED"],
      ["RETIRED", "RETIRED"],
      ["HOUSEWIFE", "HOUSEWIFE"],
      ["STUDENT", "STUDENT"],
      ["BUSINESS", "BUSINESS"],
    ],
  },
  {
    name: "religion",
    label: "Religion",
    type: "select",
    options: [
      ["HINDU", "HINDU"],
      ["MUSLIM", "MUSLIM"],
      ["CHRISTIAN", "CHRISTIAN"],
      ["SIKH", "SIKH"],
    ],
  },
  {
    name: "category",
    label: "Category",
    type: "select",
    options: [
      ["GENERAL", "GENERAL"],
      ["OBC", "OBC"],
      ["SC", "SC"],
      ["ST", "ST"],
    ],
  },
  { name: "income", label: "Monthly Income", maxLength: 36, numeric: true },
];

const allFields = [...applicantFields, ...addressFields];
const tabs = ["Applicant Details", "Address & Other Details", "Preview"];

const initialFormData = allFields.reduce((data, field) => {
  data[field.name] = field.type === "select" ? SELECT : "";
  return data;
}, {});

function validateField(field, value) {
  const text = String(value ?? "").trim();

  if (field.required) {
    if (field.type === "select" && (!value || value === SELECT)) {
      return `${field.label} is required.`;
    }

    if (!text) {
      return `${field.label} is required.`;
    }
  }

  if (
    field.alphaOnly &&
    text &&
    !/^[A-Za-z ]+$/.test(text)
  ) {
    return `${field.label} must contain alphabet letters only.`;
  }

  if (field.name === "cifno" && text && !/^\d{11}$/.test(text)) {
    return "CIF number must contain 11 digits.";
  }

  if (field.name === "acno" && text && !/^\d{11}$/.test(text)) {
    return "Account number must contain 11 digits.";
  }

  if (field.name === "aadhaar" && text && !/^\d{12}$/.test(text)) {
    return "Aadhaar number must contain exactly 12 digits.";
  }

  if (field.name === "mobile" && text && !/^\d{10}$/.test(text)) {
    return "Mobile number must contain 10 digits.";
  }

  if (field.name === "pin" && text && !/^\d{6}$/.test(text)) {
    return "PIN must contain exactly 6 digits.";
  }

  if (
    field.name === "pan" &&
    text &&
    !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(text)
  ) {
    return "Enter a valid PAN number (e.g. ABCDE1234F).";
  }

  return "";
}

function formatDate(value) {
  if (!value) return "-";
  const [year, month, day] = value.split("-");
  return year && month && day ? `${day}/${month}/${year}` : value;
}

function getOptionLabel(field, value) {
  if (!field.options || value === SELECT) return "-";
  const match = field.options.find(([optionValue]) => optionValue === value);
  return match ? match[1] : value;
}

async function parseJsonResponse(response) {
  const text = await response.text();

  if (!text.trim()) return null;

  try {
    return JSON.parse(text);
  } catch {
    throw new Error("The server returned an invalid JSON response.");
  }
}

export default function KYC3() {
  const [currentTab, setCurrentTab] = useState(0);
  const [maxAllowedTab, setMaxAllowedTab] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [pdfUrl, setPdfUrl] = useState("");
  const [showPdf, setShowPdf] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const nextButtonRef = useRef(null);

  const fieldsForTab = useMemo(
    () => ({
      0: applicantFields,
      1: addressFields,
      2: [],
    }),
    [],
  );

  const visibleFields = fieldsForTab[currentTab] || [];
  const firstFieldName = visibleFields[0]?.name || "";
  const lastFieldName = visibleFields[visibleFields.length - 1]?.name || "";

  useEffect(() => {
    if (!firstFieldName) return undefined;

    const frame = window.requestAnimationFrame(() => {
      document.getElementById(firstFieldName)?.focus();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [currentTab, firstFieldName]);

  useEffect(() => {
    const controller = new AbortController();

    async function loadAutofill() {
      try {
        const response = await fetch(
          `/backend/api/autofill.php?form=${encodeURIComponent(FORM_NAME)}`,
          {
            method: "GET",
            credentials: "include",
            cache: "no-store",
            headers: {
              Accept: "application/json",
            },
            signal: controller.signal,
          },
        );

        const result = await parseJsonResponse(response);

        if (
          !response.ok ||
          !result?.success ||
          !result?.found ||
          !result?.data
        ) {
          return;
        }

        setFormData((previous) => ({
          ...previous,
          branch: previous.branch || result.data.branch || "",
          bcode:
            previous.bcode ||
            result.data.code ||
            result.data.bcode ||
            "",
          city: previous.city || result.data.city || "",
          dist: previous.dist || result.data.dist || "",
          state: previous.state || result.data.state || "",
          pin: previous.pin || result.data.pin || "",
        }));
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("SBI KYC3 autofill error:", error);
        }
      }
    }

    loadAutofill();

    return () => controller.abort();
  }, []);

  useEffect(
    () => () => {
      if (pdfUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(pdfUrl);
      }
    },
    [pdfUrl],
  );

  const updateField = (field, rawValue) => {
    let value = rawValue;

    if (field.numeric) {
      value = value.replace(/\D/g, "");
    }

    if (field.alphaOnly) {
      value = value.replace(/[^A-Za-z ]/g, "");
    }

    if (field.uppercase || field.name.includes("pan")) {
      value = value.toUpperCase();
    }

    setFormData((previous) => ({
      ...previous,
      [field.name]: value,
    }));

    setErrors((previous) => ({
      ...previous,
      [field.name]: "",
    }));
  };

  const validateCurrentTab = () => {
    const nextErrors = {};

    visibleFields.forEach((field) => {
      const message = validateField(field, formData[field.name]);

      if (message) {
        nextErrors[field.name] = message;
      }
    });

    setErrors((previous) => ({
      ...previous,
      ...nextErrors,
    }));

    return Object.keys(nextErrors).length === 0;
  };

  const validateAllFields = () => {
    const nextErrors = {};

    allFields.forEach((field) => {
      const message = validateField(field, formData[field.name]);

      if (message) {
        nextErrors[field.name] = message;
      }
    });

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const goNext = (event) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (!validateCurrentTab()) {
      return;
    }

    const nextTab = currentTab + 1;

    if (nextTab < tabs.length) {
      setCurrentTab(nextTab);
      setMaxAllowedTab((previous) => Math.max(previous, nextTab));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const goPrevious = () => {
    if (currentTab > 0) {
      setCurrentTab((previous) => previous - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleLastFieldKeyDown = (event, fieldName) => {
    if (
      fieldName === lastFieldName &&
      event.key === "Tab" &&
      !event.shiftKey
    ) {
      event.preventDefault();
      nextButtonRef.current?.focus();
    }
  };


  // Open In PDF Viewer
  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!validateAllFields()) {
      const firstInvalidField = allFields.find(
        (field) => validateField(field, formData[field.name]),
      );

      if (firstInvalidField) {
        const invalidTab = applicantFields.some(
          (field) => field.name === firstInvalidField.name,
        )
          ? 0
          : 1;

        setCurrentTab(invalidTab);
      }

      return;
    }

    setPdfLoading(true);
    setPdfError("");

    try {
      const body = new FormData();

      Object.entries(formData).forEach(([name, value]) => {
        body.append(name, value === SELECT ? "" : String(value ?? ""));
      });

      const controller = new AbortController();
      const timeoutId = window.setTimeout(
        () => controller.abort(),
        60000,
      );

      let response;

      try {
        response = await fetch(PDF_ENDPOINT, {
          method: "POST",
          body,
          credentials: "include",
          headers: {
            Accept: "application/pdf",
          },
          signal: controller.signal,
        });
      } finally {
        window.clearTimeout(timeoutId);
      }

      if (!response.ok) {
        const message = await response.text();

        throw new Error(
          message || `PDF generation failed (${response.status}).`,
        );
      }

      const contentType =
        response.headers.get("content-type") || "";

      if (!contentType.includes("application/pdf")) {
        const message = await response.text();

        throw new Error(
          message || "Print.php did not return a PDF file.",
        );
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      setPdfUrl((previous) => {
        if (previous?.startsWith("blob:")) {
          URL.revokeObjectURL(previous);
        }

        return objectUrl;
      });

      setShowPdf(true);
    } catch (error) {
      console.error("SBI KYC3 PDF error:", error);

      if (error.name === "AbortError") {
        setPdfError(
          "PDF generation timed out. Check Print.php and release the PHP session before generating the PDF.",
        );
      } else {
        setPdfError(
          error.message || "Unable to generate PDF preview.",
        );
      }
    } finally {
      setPdfLoading(false);
    }
  };
  

//   //Open in New Tab

//   const handleSubmit = (event) => {
//   event.preventDefault();
//   event.stopPropagation();

//   // Validate all fields first
//   if (!validateAllFields()) {
//     const firstInvalidField = allFields.find((field) =>
//       validateField(field, formData[field.name])
//     );

//     if (firstInvalidField) {
//       const invalidTab = applicantFields.some(
//         (field) => field.name === firstInvalidField.name
//       )
//         ? 0
//         : 1;

//       setCurrentTab(invalidTab);
//     }

//     return;
//   }

//   // Create temporary HTML form
//   const form = document.createElement("form");

//   form.method = "POST";
//   form.action = PDF_ENDPOINT;
//   form.target = "_blank";
//   form.style.display = "none";

//   // Add all React formData values as POST fields
//   Object.entries(formData).forEach(([name, value]) => {
//     const input = document.createElement("input");

//     input.type = "hidden";
//     input.name = name;
//     input.value = value === SELECT ? "" : String(value ?? "");

//     form.appendChild(input);
//   });

//   // Add form to document
//   document.body.appendChild(form);

//   // Open Print.php/PDF in new tab
//   form.submit();

//   // Remove temporary form
//   document.body.removeChild(form);
// };




  const renderField = (field) => {
    const error = errors[field.name];

    return (
      <div key={field.name} className="space-y-2">
        <Label htmlFor={field.name}>
          {field.label}
          {field.required && (
            <span className="text-red-500"> *</span>
          )}
        </Label>

        {field.type === "select" ? (
          <select
            id={field.name}
            name={field.name}
            value={formData[field.name]}
            onChange={(event) =>
              updateField(field, event.target.value)
            }
            onKeyDown={(event) =>
              handleLastFieldKeyDown(event, field.name)
            }
            className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
              error ? "border-red-500" : ""
            }`}
          >
            <option value={SELECT}>SELECT</option>

            {field.options.map(([value, label]) => (
              <option
                key={`${field.name}-${value}`}
                value={value}
              >
                {label}
              </option>
            ))}
          </select>
        ) : (
          <Input
            id={field.name}
            name={field.name}
            type={field.type || "text"}
            value={formData[field.name]}
            maxLength={field.maxLength}
            placeholder={field.label}
            className={error ? "border-red-500" : ""}
            onChange={(event) =>
              updateField(field, event.target.value)
            }
            onKeyDown={(event) =>
              handleLastFieldKeyDown(event, field.name)
            }
          />
        )}

        {error && (
          <p className="text-sm text-red-500">{error}</p>
        )}
      </div>
    );
  };

  if (showPdf && pdfUrl) {
    return (
      <PdfViewerContainer
        pdfUrl={pdfUrl}
        title="SBI KYC Form 3"
        onBack={() => setShowPdf(false)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="mx-auto max-w-7xl">
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-3xl">
              🏦 SBI KYC Form 3
            </CardTitle>

            <p className="text-muted-foreground">
              Enter the applicant, address, and KYC details
              carefully.
            </p>
          </CardHeader>
        </Card>

        <div className="mb-8 grid grid-cols-3 gap-3">
          {tabs.map((tab, index) => (
            <Button
              key={tab}
              type="button"
              variant={
                currentTab === index ? "default" : "outline"
              }
              disabled={index > maxAllowedTab}
              onClick={() => setCurrentTab(index)}
            >
              {index + 1}. {tab}
            </Button>
          ))}
        </div>

        {currentTab === 0 && (
          <Card>
            <CardHeader>
              <CardTitle>👤 Applicant Details</CardTitle>
            </CardHeader>

            <CardContent>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {applicantFields.map(renderField)}
              </div>
            </CardContent>
          </Card>
        )}

        {currentTab === 1 && (
          <Card>
            <CardHeader>
              <CardTitle>
                🏠 Address and Other Details
              </CardTitle>
            </CardHeader>

            <CardContent>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {addressFields.map(renderField)}
              </div>
            </CardContent>
          </Card>
        )}

        {currentTab === 2 && (
          <Card>
            <CardHeader>
              <CardTitle>📋 Preview</CardTitle>
            </CardHeader>

            <CardContent className="space-y-8">
              <section>
                <h3 className="mb-3 text-lg font-semibold">
                  Applicant Details
                </h3>

                <div className="overflow-hidden rounded-lg border">
                  {applicantFields.map((field) => (
                    <div
                      key={field.name}
                      className="grid grid-cols-2 border-b p-3 last:border-b-0"
                    >
                      <span className="font-medium">
                        {field.label}
                      </span>

                      <span className="break-words">
                        {field.type === "date"
                          ? formatDate(formData[field.name])
                          : field.type === "select"
                            ? getOptionLabel(
                                field,
                                formData[field.name],
                              )
                            : formData[field.name] || "-"}
                      </span>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h3 className="mb-3 text-lg font-semibold">
                  Address and Other Details
                </h3>

                <div className="overflow-hidden rounded-lg border">
                  {addressFields.map((field) => (
                    <div
                      key={field.name}
                      className="grid grid-cols-2 border-b p-3 last:border-b-0"
                    >
                      <span className="font-medium">
                        {field.label}
                      </span>

                      <span className="break-words">
                        {field.type === "select"
                          ? getOptionLabel(
                              field,
                              formData[field.name],
                            )
                          : formData[field.name] || "-"}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            </CardContent>
          </Card>
        )}

        {pdfError && (
          <p className="mt-4 rounded-lg border border-red-300 bg-red-50 p-3 text-red-700">
            {pdfError}
          </p>
        )}

        <div className="mt-6 flex items-center justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={goPrevious}
            disabled={currentTab === 0}
          >
            ← Previous
          </Button>

          {currentTab === tabs.length - 1 ? (
            <Button
              type="button"
              ref={nextButtonRef}
              onClick={handleSubmit}
              disabled={pdfLoading}
            >
              {pdfLoading
                ? "Generating PDF..."
                : "Submit & Preview PDF"}
            </Button>
          ) : (
            <Button
              type="button"
              ref={nextButtonRef}
              onClick={goNext}
            >
              {currentTab === tabs.length - 2
                ? "Preview"
                : "Next →"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
